import {
  locationsMatchForNearMeGranularity,
  parseLocationForMatching,
  type NearMeGranularity,
} from "@/lib/push-notifications/location-matching";
import {
  comparePreferenceAttrs,
  listingAttrsForPreferenceCompare,
} from "./preferenceAttrs";
import type {
  MatchBand,
  MatchGeoTier,
  MatchListingInput,
  MatchRequestInput,
  MatchScoreBreakdown,
  ScoredMatch,
} from "./matchTypes";

const GEO_TIERS: NearMeGranularity[] = ["neighborhood", "lga", "state"];

/**
 * Best geo tier where request location matches listing location (string parse).
 * Requests have no location IDs — string path only.
 */
export function resolveMatchGeoTier(
  requestLocation: string,
  listingLocation: string,
): MatchGeoTier {
  const subscriber = parseLocationForMatching(requestLocation);
  const item = parseLocationForMatching(listingLocation);

  for (const tier of GEO_TIERS) {
    if (locationsMatchForNearMeGranularity(subscriber, item, tier)) {
      return tier;
    }
  }
  return "none";
}

function geoScore(tier: MatchGeoTier): number {
  switch (tier) {
    case "neighborhood":
      return 30;
    case "lga":
      return 20;
    case "state":
      return 10;
    default:
      return 0;
  }
}

/**
 * Score one approved listing against a request (D2 signals + D3 bands + D12 soft prefs).
 * Category mismatch → null (exclude from pull list).
 * Subcategory mismatch when request has a subcategory → null (H1/H10 — all bands).
 * Preference mismatch never excludes — soft boost only (widen within subcategory, H2).
 */
export function scoreListingAgainstRequest(
  request: MatchRequestInput,
  listing: MatchListingInput,
): ScoredMatch | null {
  const categoryMatch =
    (request.category || "").trim() === (listing.category || "").trim();
  if (!categoryMatch) return null;

  const requestSub = (request.subcategory || "").trim();
  const listingSub = (listing.subcategory || "").trim();
  // Legacy / missing subcategory on request: treat as match (parent CU5 leave-as-is).
  const subcategoryMatch = !requestSub || requestSub === listingSub;
  if (!subcategoryMatch) return null;

  const geoTier = resolveMatchGeoTier(
    request.userLocation,
    listing.location || "",
  );

  const maxPrice =
    request.maxPrice != null && Number.isFinite(Number(request.maxPrice))
      ? Number(request.maxPrice)
      : null;
  const budgetOpen = maxPrice == null;
  const price = Number(listing.price) || 0;
  const overBudget = !budgetOpen && price > maxPrice!;
  const inBudget = budgetOpen || price <= maxPrice!;

  const prefCompare = comparePreferenceAttrs(
    request.preferenceAttrs,
    listingAttrsForPreferenceCompare({
      attributes: listing.attributes,
      brand: listing.brand,
      size: listing.size,
      model: listing.model,
    }),
  );

  const breakdown: MatchScoreBreakdown = {
    categoryMatch,
    subcategoryMatch,
    geoTier,
    inBudget,
    overBudget,
    budgetOpen,
    preferenceFit: prefCompare.fit,
    preferenceKeysCompared: prefCompare.keysCompared,
    preferenceKeysMatched: prefCompare.keysMatched,
  };

  const band = assignMatchBand(breakdown);
  let score = 0;
  score += subcategoryMatch ? 40 : 0;
  score += geoScore(geoTier);
  if (inBudget) score += budgetOpen ? 10 : 20;
  if (
    listing.sellerAvailabilityConfirmedAt != null &&
    listing.sellerAvailabilityConfirmedAt !== ""
  ) {
    score += 5;
  }
  score += prefCompare.scoreBoost;

  return { listing, band, score, breakdown };
}

/**
 * Band thresholds (locked in phase-2-match-thresholds.md; UX hardening Phase 1):
 * - over_budget: subcategory OK (when request has one) + price > max
 * - strong: subcat OK + geo ≥ lga + in budget
 * - soft: subcat OK + in budget + geo ≥ state (widen = prefs within type, not other appliances)
 * - exploratory: subcat OK + in budget, weaker geo
 * Preference attrs do not change band eligibility (widen path within subcategory).
 */
export function assignMatchBand(b: MatchScoreBreakdown): MatchBand {
  if (b.overBudget) return "over_budget";

  // Defensive: scoreListingAgainstRequest excludes subcategory mismatches.
  if (!b.subcategoryMatch) return "exploratory";

  const geoAtLeastLga = b.geoTier === "neighborhood" || b.geoTier === "lga";
  const geoAtLeastState = geoAtLeastLga || b.geoTier === "state";

  if (geoAtLeastLga && b.inBudget) {
    return "strong";
  }
  if (b.inBudget && geoAtLeastState) {
    return "soft";
  }
  return "exploratory";
}
